import React, { useState } from "react";
import { Button, Modal, SelectChangeEvent } from "@mui/material";
import addVideoIcon from "@/assets/icons/addvideo.svg";
import imgIcon from "@/assets/icons/image-upload.svg";
import txtIcon from "@/assets/icons/txt-upload.svg";
import styles from "./AddVideoModal.module.scss";
import Input from "@/components/Input/Input";
import Textarea from "@/components/Textarea/Textarea";
import { notification } from "antd";
import CustomSelect from "@/components/Select/Select";
import { MediaAccessType } from "@/store/models/video.models";
import { typeOptions } from "@/pages/MusicPage/components/AddMusicModal/AddMusicModal";

type Props = {
  isOpenAddItemModal: boolean;
  setIsOpenAddItemModal: (isOpenAddItemModal: boolean) => void;
  onAddVideo: (formData: FormData) => Promise<void>;
};

const AddVideoModal = ({
  isOpenAddItemModal,
  setIsOpenAddItemModal,
  onAddVideo,
}: Props) => {
  const [videoLink, setVideoLink] = useState<File | string>("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [subtitles, setSubtitles] = useState<File | string>("");
  const [thumbnail, setThumbnail] = useState<File | string>("");
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [videoName, setVideoName] = useState<string | null>(null);
  const [subtitleName, setSubtitleName] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState(MediaAccessType.BASE);

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setVideoLink(file);
      setVideoName(file.name);
    } else {
      setVideoLink("");
      setVideoName(null);
    }
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setThumbnail(file);
      setThumbnailPreview(URL.createObjectURL(file));
    } else {
      setThumbnail("");
      setThumbnailPreview(null);
    }
  };
  const handleRoleChange = (event: SelectChangeEvent<MediaAccessType>) => {
    const type = event.target.value as MediaAccessType;
    setSelectedRole(type);
  };

  const handleSubtitlesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSubtitles(file);
      setSubtitleName(file.name);
    } else {
      setSubtitles("");
      setSubtitleName(null);
    }
  };

  const handleSave = async () => {
    const formData = new FormData();
    if (videoLink instanceof File) {
      formData.append("videoSrc", videoLink);
    } else if (typeof videoLink === "string" && videoLink.trim() !== "") {
      formData.append("videoSrcUrl", videoLink);
    } else {
      notification.error({
        message: "Error",
        description: "You must provide either a video file or a video URL.",
      });
      return;
    }

    formData.append("title", title);
    formData.append("description", description);
    formData.append("accessType", selectedRole);

    if (thumbnail instanceof File) {
      formData.append("videoImg", thumbnail);
    } else {
      formData.append("thumbnail", thumbnail as string);
    }

    if (subtitles instanceof File) {
      formData.append("subtitles", subtitles);
    } else {
      formData.append("subtitles", subtitles as string);
    }
    setIsOpenAddItemModal(false);
    try {
      await onAddVideo(formData);
      resetForm();
      notification.success({
        message: "Success",
        description: "Video is loading!",
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description:
          error?.message || "Failed to create new video. Please try again.",
      });
    }
  };

  const resetForm = () => {
    setVideoLink("");
    setTitle("");
    setDescription("");
    setThumbnail("");
    setThumbnailPreview(null);
    setVideoName(null);
    setSubtitles("");
    setSubtitleName(null);
    setIsOpenAddItemModal(false);
  };

  return (
    <Modal
      aria-labelledby="modal-adding-item-to-content"
      aria-describedby="Add an item to the system. Enter the required data and click save"
      open={isOpenAddItemModal}
      onClose={() => setIsOpenAddItemModal(false)}
    >
      <div className={styles.modalAddContainer}>
        <h3 className={styles.modalTitle}>Add item</h3>
        <label
          className={styles.modalAddItem}
          htmlFor="video"
          id="dropcontainer"
        >
          <img className={styles.modalImg} src={addVideoIcon} alt="video" />
          <input
            id="video"
            type="file"
            accept="video/*"
            onChange={handleVideoChange}
          />

          <span className={styles.modalPrimaryText}>
            or drag and drop the file here (*.mp4)
          </span>
        </label>
        {videoName && (
          <p className={styles.previewVideo}>Selected video: {videoName}</p>
        )}
        {/* This functionality is not working yet */}
        {/* <p className={styles.modalHelper}>or add a link to the file</p>
        <Input
          label=""
          value={videoLink instanceof File ? "" : (videoLink as string)}
          onChange={(e) => setVideoLink(e.target.value)}
          placeholder="Enter your link"
          disabled={videoLink instanceof File}
        /> */}
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter the title"
        />
        <Textarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <div className={styles.settings}>
          <CustomSelect
            label="Access type"
            options={typeOptions}
            value={selectedRole}
            onChange={handleRoleChange}
          />
        </div>

        <label
          className={styles.modalAddItem}
          htmlFor="thumbnail"
          id="dropcontainer"
        >
          <img className={styles.modalImg} src={imgIcon} alt="thumbnail" />
          <input
            id="thumbnail"
            type="file"
            accept="image/*"
            onChange={handleThumbnailChange}
          />

          <span className={styles.modalPrimaryText}>
            or drag and drop the thumbnail file here (*.png)
          </span>
        </label>
        {thumbnailPreview && (
          <img
            className={styles.thumbnailPreview}
            src={thumbnailPreview}
            alt="Thumbnail Preview"
            width={100}
            height={100}
          />
        )}
        {/* This functionality is not working yet */}
        {/* <p className={styles.modalHelper}>or add a link to the file</p>
        <Input
          label=""
          value={thumbnail instanceof File ? "" : (thumbnail as string)}
          onChange={(e) => setThumbnail(e.target.value)}
          placeholder="Enter the thumbnail link"
          disabled={thumbnail instanceof File}
        /> */}
        <label
          className={styles.modalAddItem}
          htmlFor="subtitles"
          id="dropcontainer"
        >
          <img className={styles.modalImg} src={txtIcon} alt="subtitles" />
          <input
            id="subtitles"
            type="file"
            accept=".srt"
            onChange={handleSubtitlesChange}
          />

          <span className={styles.modalPrimaryText}>
            or drag and drop the subtitles here (*.srt)
          </span>
        </label>
        {subtitleName && (
          <p className={styles.previewVideo}>
            Selected subtitles: {subtitleName}
          </p>
        )}
        {/* This functionality is not working yet */}
        {/* <p className={styles.modalHelper}>or add a link to the file</p>
        <Input
          label=""
          value={subtitles instanceof File ? "" : (subtitles as string)}
          onChange={(e) => setSubtitles(e.target.value)}
          placeholder="Enter the subtitles link"
          disabled={subtitles instanceof File}
        /> */}
        <div className={styles.btns}>
          <Button
            sx={{
              textTransform: "capitalize",
              height: "44px",
              width: "150px",
              backgroundColor: "#008000",
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: "#008000",
              },
            }}
            variant="contained"
            onClick={handleSave}
          >
            Save
          </Button>

          <Button
            variant="outlined"
            sx={{
              textTransform: "capitalize",
              height: "44px",
              width: "150px",
              border: "1px solid #5570F1",
              color: "#5570F1",
              borderRadius: "12px",
            }}
            onClick={() => setIsOpenAddItemModal(false)}
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddVideoModal;

